import type { ClientLibraryId } from 'types/client-library'
import type { Thunk } from 'types/redux'
import type { AppState } from '@disco/state-management/reducers/root'
import { mapResponseToReducerObjects } from '@disco/state-management/actions/PlaylistTrackActions'
import api from '@disco/api/server'
import {
  CLIENT_LIBRARY_FEATURED_TRACKS_LOAD,
  CLIENT_LIBRARY_FEATURED_TRACKS_LOAD_FAIL,
  CLIENT_LIBRARY_FEATURED_TRACKS_LOAD_SUCCESS,
  CLIENT_LIBRARY_TRACKS_LOAD,
  CLIENT_LIBRARY_TRACKS_LOAD_FAIL,
  CLIENT_LIBRARY_TRACKS_LOAD_NEXT,
  CLIENT_LIBRARY_TRACKS_LOAD_NEXT_FAIL,
  CLIENT_LIBRARY_TRACKS_LOAD_NEXT_SUCCESS,
  CLIENT_LIBRARY_TRACKS_LOAD_SUCCESS,
  CLIENT_LIBRARY_TRACKS_SET,
  CLIENT_LIBRARY_TRACKS_SET_FAIL,
  CLIENT_LIBRARY_TRACKS_SET_SUCCESS,
  CLIENT_LIBRARY_TRACKS_UPDATE,
  CLIENT_LIBRARY_TRACKS_UPDATE_FAIL,
  CLIENT_LIBRARY_TRACKS_UPDATE_SUCCESS,
  TRACK_LOAD_BATCH,
} from '@disco/constants/ActionTypes'
import { TRACK } from '@disco/constants/ItemTypes'
import { omit } from '@disco/utils/array'
import { pushUINotification } from '@disco/utils/notifications'
import { Track } from '@disco/types/types/track'
/**
 * Loads tracks for a client library with search filtering.
 * @param libraryId - ID of the client library to load tracks for
 * @param query - Search query to filter tracks
 * @param abortSignal - Optional AbortSignal to cancel the request
 * @returns Thunk that loads tracks and maps them to reducer objects
 */
export function clientLibraryTracksLoad(
  libraryId: ClientLibraryId,
  query: string,
  abortSignal?: AbortSignal,
): Thunk {
  return async (dispatch, getState) => {
    dispatch({
      type: CLIENT_LIBRARY_TRACKS_LOAD,
      libraryId,
    })

    try {
      const response = await api.getClientLibraryTracks(
        libraryId,
        query,
        undefined,
        abortSignal,
      )
      mapResponseToReducerObjects(dispatch, getState, {
        ...response,
        results: response.results.map(item => omit(item, ['is_featured'])),
      })
      dispatch({
        type: CLIENT_LIBRARY_TRACKS_LOAD_SUCCESS,
        libraryId,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_TRACKS_LOAD_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Loads the next page of tracks for a client library using pagination.
 * @param libraryId - ID of the client library to load next tracks for
 * @param url - Pagination URL for the next page of tracks
 * @returns Thunk that loads next page of tracks
 */
export function clientLibraryTracksLoadNext(
  libraryId: ClientLibraryId,
  url: string,
): Thunk {
  return async (dispatch, getState) => {
    dispatch({
      type: CLIENT_LIBRARY_TRACKS_LOAD_NEXT,
      libraryId,
    })

    try {
      const response = await api.getUrl<any>(
        url,
        null,
        'api.getClientLibraryTracksLoadNext',
      )
      mapResponseToReducerObjects(dispatch, getState, {
        ...response,
        results: response.results.map(item => omit(item, ['is_featured'])),
      })
      dispatch({
        type: CLIENT_LIBRARY_TRACKS_LOAD_NEXT_SUCCESS,
        libraryId,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_TRACKS_LOAD_NEXT_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Loads featured tracks for a client library with search filtering.
 * @param libraryId - ID of the client library to load featured tracks for
 * @param query - Search query to filter featured tracks
 * @returns Thunk that loads featured tracks and maps them to reducer objects
 */
export function clientLibraryFeaturedTracksLoad(
  libraryId: ClientLibraryId,
  query: string,
): Thunk {
  return async (dispatch, getState) => {
    dispatch({
      type: CLIENT_LIBRARY_FEATURED_TRACKS_LOAD,
      libraryId,
    })

    try {
      const response = await api.getClientLibraryTracks(libraryId, query, true)
      mapResponseToReducerObjects(dispatch, getState, {
        ...response,
        results: response.results.map(item => omit(item, ['is_featured'])),
      })
      dispatch({
        type: CLIENT_LIBRARY_FEATURED_TRACKS_LOAD_SUCCESS,
        libraryId,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_FEATURED_TRACKS_LOAD_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Updates featured tracks in a client library by adding and removing specified tracks.
 * @param libraryId - ID of the client library to update tracks for
 * @param to_add - Array of track IDs to add to featured tracks
 * @param to_remove - Array of track IDs to remove from featured tracks
 * @returns Thunk that updates featured tracks and shows success notification
 */
export function clientLibraryTracksUpdate(
  libraryId: ClientLibraryId,
  to_add: number[],
  to_remove: number[],
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_TRACKS_UPDATE,
      libraryId,
      payload: {
        to_add,
        to_remove,
      },
    })

    try {
      await api.alterClientLibraryFeaturedTracks(libraryId, to_add, to_remove)
      dispatch({
        type: CLIENT_LIBRARY_TRACKS_UPDATE_SUCCESS,
        libraryId,
      })
      pushUINotification({
        message: 'Tracks updated!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_TRACKS_UPDATE_FAIL,
        libraryId,
        error,
      })
    }
  }
}
const getFeaturedTrackIds = (
  state: AppState,
  libraryId: ClientLibraryId,
): number[] =>
  state.clientLibraries.libraries.items[libraryId]?.tracks?.featured?.list ?? []

/**
 * Publishes and features individual tracks in a client library, or unfeatures and
 * unpublishes them. The change shows straight away and is undone if saving fails.
 * Callers must wait for one featured track change to settle before starting another,
 * as the undo restores the list from before this change.
 * @param libraryId - ID of the client library to update
 * @param to_add - Track IDs to publish and feature, from anywhere in the business library
 * @param to_remove - Track IDs to unfeature and unpublish
 * @returns Thunk that resolves to the added track IDs the server skipped (e.g. over quota)
 * once the change is saved, or rejects if it couldn't be saved
 */
export function clientLibraryFeaturedTracksAlter(
  libraryId: ClientLibraryId,
  to_add: number[],
  to_remove: number[],
): Thunk {
  return async (dispatch, getState: () => AppState) => {
    const previousIds = getFeaturedTrackIds(getState(), libraryId)
    dispatch({
      type: CLIENT_LIBRARY_TRACKS_UPDATE,
      libraryId,
      payload: {
        to_add,
        to_remove,
      },
    })

    let response: Awaited<ReturnType<typeof api.alterClientLibraryTracks>>

    try {
      response = await api.alterClientLibraryTracks(libraryId, {
        to_add,
        to_remove,
        feature: true,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_TRACKS_UPDATE_FAIL,
        libraryId,
        error,
      })
      dispatch({
        type: CLIENT_LIBRARY_TRACKS_SET,
        libraryId,
        trackIds: previousIds,
      })
      throw error
    }

    dispatch({
      type: CLIENT_LIBRARY_TRACKS_UPDATE_SUCCESS,
      libraryId,
    })
    // Featured tracks are normally loaded from search, which is reindexed
    // asynchronously, so take the list from the response instead of reloading
    dispatch({
      type: CLIENT_LIBRARY_TRACKS_SET,
      libraryId,
      trackIds: response.featured_track_ids,
    })

    return { skippedTrackIds: response.skipped_track_ids }
  }
}

/**
 * Sets the order of a client library's featured tracks. The new order shows straight
 * away and is undone if saving fails. Callers must wait for one featured track change
 * to settle before starting another, as the undo restores the order from before this change.
 * @param libraryId - ID of the client library to update
 * @param trackIds - All featured track IDs in their new order
 * @returns Thunk that resolves once the order is saved, or rejects if it couldn't be saved
 */
export function clientLibraryFeaturedTracksReorder(
  libraryId: ClientLibraryId,
  trackIds: number[],
): Thunk {
  return async (dispatch, getState: () => AppState) => {
    const previousIds = getFeaturedTrackIds(getState(), libraryId)
    dispatch({
      type: CLIENT_LIBRARY_TRACKS_SET,
      libraryId,
      trackIds,
    })

    try {
      await api.setClientLibraryFeaturedTracks(libraryId, trackIds)
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_TRACKS_SET_FAIL,
        libraryId,
        error,
      })
      dispatch({
        type: CLIENT_LIBRARY_TRACKS_SET,
        libraryId,
        trackIds: previousIds,
      })
      throw error
    }

    dispatch({
      type: CLIENT_LIBRARY_TRACKS_SET_SUCCESS,
      libraryId,
    })
  }
}
/**
 * Sets the complete list of featured tracks for a client library.
 * @param libraryId - ID of the client library to set featured tracks for
 * @param trackIds - Array of track IDs to set as featured tracks
 * @returns Thunk that sets featured tracks and shows success notification
 */
export function clientLibraryTracksSet(
  libraryId: ClientLibraryId,
  trackIds: number[],
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_TRACKS_SET,
      libraryId,
      trackIds,
    })

    try {
      await api.setClientLibraryFeaturedTracks(libraryId, trackIds)
      dispatch({
        type: CLIENT_LIBRARY_TRACKS_SET_SUCCESS,
        libraryId,
      })
      pushUINotification({
        message: 'Tracks updated!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_TRACKS_SET_FAIL,
        libraryId,
        error,
      })
    }
  }
}

export function getClientLibraryTracksFromExternalUrl(
  catalogId: ClientLibraryId,
  catalogUrl: string,
  count: number,
): Thunk {
  return async dispatch => {
    if (catalogUrl && catalogId) {
      try {
        // Process all catalogs and accumulate their tracks
        const allCatalogTracks = {}

        // Await the API response
        const response = await api.getClientLibraryFeaturedTracks(
          catalogUrl,
          catalogId,
          count,
        )

        // Check the response structure and extract tracks appropriately
        let tracks: Track[] = []
        if (response) {
          tracks = response.results?.tracks || []
        }

        // Format tracks with proper type and dispatch batch load
        // External tracks don't have groups, so we default to empty array
        const tracksWithType = tracks.map(track => ({
          ...track,
          type: TRACK,
          groups: track.groups ?? [],
        }))

        dispatch({
          type: TRACK_LOAD_BATCH,
          list: tracksWithType,
          resetPlaylistInfo: false,
        })

        allCatalogTracks[catalogId] = tracks
        return allCatalogTracks
      } catch (error) {
        console.error('Error loading tracks from public_url:', error)
        throw error
      }
    }
  }
}
